import { useState } from "react";
import { Bookmark, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const KEY = "evidex_ticket_presets";
const FIELDS = ["product_id", "side", "type", "sizeMode", "base_size", "quote_size", "limit_price", "stop_price", "take_profit", "stop_loss", "trail_pct"];
const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const save = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* storage blocked */ } };
const label = (p) => {
  const size = p.type === "market" && p.sizeMode === "quote" ? (p.quote_size ? `$${Number(p.quote_size).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "") : (p.base_size ? `${p.base_size} ${p.product_id.split("-")[0]}` : "");
  return [p.side, p.product_id, p.type === "market" ? "" : p.type.replace("_", "-"), size].filter(Boolean).join(" · ");
};
const shown = (p) => p.name || label(p);

export const TicketPresets = ({ form, onLoad }) => {
  const [presets, setPresets] = useState(load);
  const [editing, setEditing] = useState(null);  // index being renamed
  const [draft, setDraft] = useState("");
  const hasSize = form.type === "market" && form.sizeMode === "quote" ? !!form.quote_size : !!form.base_size;
  const persist = (next) => { setPresets(next); save(next); };
  const add = () => {
    const p = Object.fromEntries(FIELDS.map((k) => [k, form[k]]));
    const next = [p, ...presets.filter((x) => label(x) !== label(p))].slice(0, 6);
    persist(next); toast.success(`Saved "${label(p)}" — click its name to rename`);
  };
  const remove = (i) => persist(presets.filter((_, j) => j !== i));
  const startRename = (i) => { setEditing(i); setDraft(presets[i].name || ""); };
  const commitRename = () => {
    if (editing === null) return;
    const name = draft.trim().slice(0, 40);
    persist(presets.map((p, j) => (j === editing ? { ...p, name: name || undefined } : p)));
    setEditing(null);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cb-presets">
      <button type="button" data-testid="cb-preset-save" onClick={add} disabled={!hasSize} title={hasSize ? "Save this ticket as a one-tap preset" : "Enter a size first"} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-[11px] text-slate-300 hover:border-cyan-700 hover:text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        <Bookmark className="w-3 h-3" /> Save ticket
      </button>
      {presets.map((p, i) => (
        <span key={label(p)} className={`inline-flex items-center rounded-full border text-[11px] font-mono-ticker overflow-hidden ${p.side === "BUY" ? "border-emerald-900 text-emerald-200" : "border-rose-900 text-rose-200"}`} title={p.name ? label(p) : undefined}>
          {editing === i ? (
            <input autoFocus data-testid={`cb-preset-name-input-${i}`} value={draft} maxLength={40} placeholder={label(p)} onChange={(e) => setDraft(e.target.value)} onBlur={commitRename}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitRename(); } if (e.key === "Escape") setEditing(null); }}
              className="bg-slate-950 px-2.5 py-1 w-44 text-slate-100 outline-none" />
          ) : (
            <button type="button" data-testid={`cb-preset-${i}`} onClick={() => onLoad(p)} title={`Load this ticket${p.name ? ` (${label(p)})` : ""}`} className="px-2.5 py-1 hover:bg-slate-800/60 transition-colors">{shown(p)}</button>
          )}
          <button type="button" data-testid={`cb-preset-rename-${i}`} onClick={() => startRename(i)} aria-label={`Rename preset ${shown(p)}`} title="Rename" className="px-1.5 py-1 text-slate-500 hover:text-cyan-300 border-l border-slate-800 transition-colors"><Pencil className="w-3 h-3" /></button>
          <button type="button" data-testid={`cb-preset-remove-${i}`} onClick={() => remove(i)} aria-label={`Remove preset ${shown(p)}`} className="px-1.5 py-1 text-slate-500 hover:text-rose-300 border-l border-slate-800 transition-colors"><X className="w-3 h-3" /></button>
        </span>
      ))}
      {presets.length === 0 && <span className="text-[11px] text-slate-500">Save a filled-in ticket to reopen it with one tap.</span>}
    </div>
  );
};
